import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Trash2, Send, Loader2, Package, MessageSquare } from "lucide-react";
import api, { usd, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const BLANK = { title: "", category: "rifle", manufacturer: "", caliber: "", condition: "New", price: "", description: "", image: "", location: "USA" };

export default function Dashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("offers");
  const [listings, setListings] = useState([]);
  const [offers, setOffers] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");

  const loadListings = () => api.get("/my/listings").then((r) => setListings(r.data));
  const loadOffers = () => api.get("/offers").then((r) => setOffers(r.data));

  useEffect(() => { loadListings(); loadOffers(); }, []);

  const createListing = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/listings", { ...form, price: parseFloat(form.price) });
      toast.success("Listing posted to the marketplace.");
      setForm(BLANK); loadListings(); setTab("listings");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const removeListing = async (id) => {
    try { await api.delete(`/listings/${id}`); toast.success("Listing removed."); loadListings(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendReply = async (offerId) => {
    if (!reply.trim()) return;
    try {
      await api.post(`/offers/${offerId}/messages`, { text: reply });
      setReply("");
      const { data } = await api.get("/offers");
      setOffers(data);
      setActive(data.find((o) => o.id === offerId));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const respond = async (offerId, status) => {
    try {
      await api.post(`/offers/${offerId}/status`, { status });
      toast.success(`Offer ${status}.`);
      const { data } = await api.get("/offers");
      setOffers(data);
      setActive(data.find((o) => o.id === offerId));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const TABS = [
    { id: "offers", label: "Offers & Chat", icon: MessageSquare },
    { id: "listings", label: "My Listings", icon: Package },
    { id: "sell", label: "Post Iron", icon: Plus },
  ];

  return (
    <div className="relative z-10 pt-28 md:pt-32 pb-24 max-w-[1400px] mx-auto px-5 md:px-10">
      <div className="mono-label mb-3">/ {user?.email}</div>
      <h1 className="font-display uppercase text-5xl sm:text-7xl mb-10">The Desk</h1>

      <div className="flex flex-wrap gap-2 mb-10 border-b border-[#5A2A24] pb-5">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 h-11 border transition-colors ${
              tab === t.id ? "bg-[#C0894F] border-[#C0894F] text-white" : "border-[#5A2A24] text-[#C7A98F] hover:border-[#EFE4D2] hover:text-[#EFE4D2]"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* OFFERS */}
      {tab === "offers" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 space-y-3">
            {offers.length === 0 && <div className="font-mono text-[#8C6552] py-10" data-testid="no-offers">No offers yet. Browse the marketplace and make one.</div>}
            {offers.map((o) => (
              <button key={o.id} onClick={() => setActive(o)} data-testid={`offer-${o.id}`}
                className={`w-full text-left border p-4 flex gap-4 transition-colors ${active?.id === o.id ? "border-[#C0894F] bg-[#2C0D12]" : "border-[#5A2A24] hover:border-[#7A4033]"}`}>
                <img src={o.listing_image} alt="" className="w-16 h-16 object-cover border border-[#5A2A24]" />
                <div className="flex-1 min-w-0">
                  <div className="font-display uppercase text-lg truncate">{o.listing_title}</div>
                  <div className="font-mono text-[#C0894F]">{usd(o.amount)}</div>
                  <div className="mono-label mt-1">{o.role === "seller" ? "Incoming" : "Sent"} · {o.status}</div>
                </div>
              </button>
            ))}
          </div>

          <div className="lg:col-span-7">
            {active ? (
              <div className="border border-[#5A2A24]">
                <div className="p-5 border-b border-[#5A2A24] flex items-center justify-between gap-4">
                  <div>
                    <Link to={`/listing/${active.listing_id}`} className="font-display uppercase text-xl hover:text-[#C0894F] transition-colors">{active.listing_title}</Link>
                    <div className="mono-label mt-1">Offer: <span className="text-[#C0894F]">{usd(active.amount)}</span> · Status: {active.status}</div>
                  </div>
                  {active.role === "seller" && active.status === "pending" && (
                    <div className="flex gap-2">
                      <button onClick={() => respond(active.id, "accepted")} data-testid="accept-offer" className="btn-tactical h-10 !px-4">Accept</button>
                      <button onClick={() => respond(active.id, "declined")} data-testid="decline-offer" className="btn-ghost h-10 !px-4">Decline</button>
                    </div>
                  )}
                </div>
                <div className="p-5 space-y-3 max-h-[380px] overflow-y-auto" data-testid="chat-thread">
                  {active.messages?.length ? active.messages.map((m, i) => (
                    <div key={i} className={`flex ${m.sender_id === user.id ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] p-3 text-sm ${m.sender_id === user.id ? "bg-[#C0894F] text-white" : "bg-[#371017] text-[#EFE4D2] border border-[#5A2A24]"}`}>
                        <div className="mono-label mb-1 opacity-70">{m.sender_name}</div>
                        {m.text}
                      </div>
                    </div>
                  )) : <div className="font-mono text-[#8C6552] text-sm">No messages yet. Start the negotiation.</div>}
                </div>
                <div className="p-4 border-t border-[#5A2A24] flex gap-2">
                  <input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply(active.id)}
                    placeholder="Type a message…" data-testid="reply-input" className="field flex-1" />
                  <button onClick={() => sendReply(active.id)} data-testid="send-reply" className="btn-tactical !px-4"><Send className="w-4 h-4" /></button>
                </div>
              </div>
            ) : (
              <div className="border border-[#5A2A24] h-full min-h-[300px] flex items-center justify-center font-mono text-[#8C6552]">Select an offer to open the thread.</div>
            )}
          </div>
        </div>
      )}

      {/* LISTINGS */}
      {tab === "listings" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {listings.length === 0 && <div className="font-mono text-[#8C6552] py-10" data-testid="no-listings">No listings yet. Post your first piece.</div>}
          {listings.map((l) => (
            <div key={l.id} className="border border-[#5A2A24] bg-[#2C0D12]">
              <img src={l.image} alt={l.title} className="w-full aspect-[4/3] object-cover" />
              <div className="p-4">
                <div className="font-display uppercase text-lg">{l.title}</div>
                <div className="font-mono text-[#C0894F] mb-3">{usd(l.price)}</div>
                <div className="flex items-center justify-between">
                  <span className="mono-label">{l.status}</span>
                  <button onClick={() => removeListing(l.id)} data-testid={`delete-${l.id}`} className="text-[#8C6552] hover:text-[#C0894F] transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* SELL */}
      {tab === "sell" && (
        <motion.form onSubmit={createListing} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl grid grid-cols-1 md:grid-cols-2 gap-4">
          {[["title", "Title", "text", 2], ["manufacturer", "Manufacturer", "text", 1], ["caliber", "Caliber", "text", 1], ["price", "Price (USD)", "number", 1], ["location", "Location", "text", 1], ["image", "Image URL", "text", 2]].map(([k, label, type, span]) => (
            <div key={k} className={span === 2 ? "md:col-span-2" : ""}>
              <label className="mono-label block mb-1">{label}</label>
              <input required type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`sell-${k}`} className="field" />
            </div>
          ))}
          <div>
            <label className="mono-label block mb-1">Category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="sell-category" className="field">
              {["rifle", "pistol", "revolver"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="mono-label block mb-1">Condition</label>
            <select value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} data-testid="sell-condition" className="field">
              {["New", "Like New", "Used - Excellent", "Used - Good"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="mono-label block mb-1">Description</label>
            <textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} data-testid="sell-description" className="field !h-auto py-3 resize-none" />
          </div>
          <div className="md:col-span-2">
            <button type="submit" disabled={saving} data-testid="sell-submit" className="btn-tactical w-full disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post Listing"}
            </button>
          </div>
        </motion.form>
      )}
    </div>
  );
}
